import PageLoader from '@/components/shared/PageLoader';
import { getCity } from '@/components/utils';
import { VillageType, RoleType, Village } from '@/constants';
import { API_USER_PROFILE } from '@/services/APIs';
import { fetchAllCities } from '@/services/basic';
import { fetchGET } from '@/services/fetch';
import { useRequest } from 'ahooks';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ItemDataType } from 'rsuite5/esm/@types/common';

export interface Profile {
  user_name: string;
  avatar: string;
  shopping_village: VillageType; // 购物村 1苏州 2上海
  service_site_name: string; // 服务站点名称
  role: RoleType; // 账户部门
  [key: string]: any;
}

/**
 * 城市数据
 */
export interface City {
  label: string;
  value: string;
  children?: City[];
}

/**
 * 停车优惠basic数据
 */
export interface ParkingBasic {
  reason_list: ItemDataType[];
}

/**
 * 会员详情basic数据
 */
export interface AccountBasic {
  adjust_course: ItemDataType[];
}

/**
 * 黑书会员下拉数据
 */
export interface BlackBookMbrList {
  reason_list: ItemDataType[];
}

/**
 * 服务basic数据
 */
export interface ServiceBasic {
  service_status: ItemDataType[];
  service_type: ItemDataType[];
}

/**
 * 大使列表数据
 */
export interface GePcsList {
  ge_list: ItemDataType[];
  pcs_list: ItemDataType[];
  sa_list: ItemDataType[];
}

/**
 * 租赁basic数据
 */
export interface LeaseBasic {
  pledge_type: ItemDataType[]; // 质押方式
  item_type: ItemDataType[]; // 租赁物品
}

/**
 * 商品快递basic数据
 */
export interface ExpressBasic {
  courier_company: ItemDataType[];
}

/**
 * 投诉basic数据
 */
export interface CasesBasic {
  retails: ItemDataType[];
  dispose_type: ItemDataType[];
  source: ItemDataType[];
  cases_status: ItemDataType[];
  category: ItemDataType[];
}

/**
 * 失物招领basic数据
 */
export interface LoseBasic {
  pick_team: ItemDataType[]; // 拾到人所在团队
  lose_status: ItemDataType[]; // 服务状态
  deposit_addr: ItemDataType[]; // 存放地点
  item_type: ItemDataType[]; // 物品类型
}

/**
 * 预约项目和预约状态数据
 */
export interface SubscribeOption {
  option_list: ItemDataType[]; // 预约项目
  status_list: ItemDataType[]; // 预约状态
  update_status_list: ItemDataType[]; // 更新状态
}

export interface ReadyContextValue {
  /**
   * 用户信息
   */
  profile?: Profile;
  setProfile?: (profile: Partial<Profile>) => void;
  /**
   * 服务basic
   * 服务项目
   */
  serviceBasic?: ServiceBasic;
  setServiceBasic?: (serviceBasic: ServiceBasic) => void;
  /**
   * 城市数据
   */
  allCities?: City[];
  setAllCities?: (allCities: Partial<City[]>) => void;
  /**
   * 停车优惠basic
   * - 优惠原因
   */
  parkingBasic?: ParkingBasic;
  setParkingBasic?: (parkingBasic: ParkingBasic) => void;
  /**
   * 会员详情basic
   * - 等级调整原因
   */
  accountBasic?: AccountBasic;
  setAccountBasic?: (accountBasic: AccountBasic) => void;
  /**
   * 黑书会员下拉框数据
   */
  blackBookMemberList?: BlackBookMbrList;
  setBlackBookMemberList?: (blackBookMemberList: BlackBookMbrList) => void;
  /**
   * 获取入会礼包列表数据
   */
  mbrCeremonyList?: any[];
  setMbrCeremonyList?: (mbrCeremonyList: any[]) => void;
  /**
   * 大使列表数据
   */
  gePcsList?: GePcsList;
  setGePcsList?: (gePcsList: GePcsList) => void;
  /**
   * 租赁basic
   */
  leaseBasic?: LeaseBasic;
  setLeaseBasic?: (leaseBasic: LeaseBasic) => void;
  /**
   * 现场服务品牌数据
   */
  siteBrandList?: ItemDataType[];
  setSiteBrandList?: (brandList: ItemDataType[]) => void;
  /**
   * 商品快递basic
   * - 快递公司
   */
  expressBasic?: ExpressBasic;
  setExpressBasic?: (expressBasic: ExpressBasic) => void;
  /**
   * 兑换平台数据
   */
  exchangePlatform?: ItemDataType[];
  setExchangePlatform?: (brandList: ItemDataType[]) => void;
  /**
   * 失物招领basic
   */
  loseBasic?: LoseBasic;
  setLoseBasic?: (loseBasic: LoseBasic) => void;
  /**
   * 投诉basic
   */
  casesBasic?: CasesBasic;
  setCasesBasic?: (casesBasic: CasesBasic) => void;
  /**
   * 历史小票列表中获取品牌数据
   */
  receiptBrandList?: ItemDataType[];
  setReceiptBrandList?: (brandList: ItemDataType[]) => void;
  /**
   * 小票审核查询表单租赁品牌数据
   */
  serviceBrandList?: ItemDataType[];
  setServiceBrandList?: (brandList: ItemDataType[]) => void;
  /**
   * 上传小票销售渠道数据
   */
  salesSource?: ItemDataType[];
  setSalesSource?: (salesSource: ItemDataType[]) => void;
  /**
   * 小票审核拒绝原因数据
   */
  refuseDesc?: ItemDataType[];
  setRefuseDesc?: (refuseDesc: ItemDataType[]) => void;
  /**
   * 兑换管理优惠券下拉数据
   */
  ticketList?: ItemDataType[];
  setTicketList?: (ticketActList: ItemDataType[]) => void;
  /**
   * 兑换管理活动下拉数据
   */
  ticketActList?: ItemDataType[];
  setTicketActList?: (ticketActList: ItemDataType[]) => void;
  /**
   * 预约项目和预约状态数据
   */
  subscribeOption?: SubscribeOption;
  setSubscribeOption?: (subscribeOption: SubscribeOption) => void;
}

export const ReadyContext = React.createContext<ReadyContextValue>(null);

export const useReadyInfo = () => useContext<ReadyContextValue>(ReadyContext);

export const useProfile = () => (useReadyInfo()?.profile || {}) as Profile;

export const useAllCities = () => (useReadyInfo()?.allCities || {}) as City[];

interface ReadyContextProps {
  children: React.ReactChild;
}

// 接口实现请遵循 ready.js 规范
export default React.memo<ReadyContextProps>(function ReadyProvider({
  children,
}: ReadyContextProps) {
  const [appReady, setAppReady] = useState(false);
  const [profile, setProfile] = useState<Profile>();
  const [allCities, setAllCities] = useState<City[]>();
  const [parkingBasic, setParkingBasic] = useState<ParkingBasic>();
  const [accountBasic, setAccountBasic] = useState<AccountBasic>();
  const [blackBookMemberList, setBlackBookMemberList] = useState<BlackBookMbrList>();
  const [mbrCeremonyList, setMbrCeremonyList] = useState<any[]>();
  const [serviceBasic, setServiceBasic] = useState<ServiceBasic>();
  const [gePcsList, setGePcsList] = useState<GePcsList>();
  const [leaseBasic, setLeaseBasic] = useState<LeaseBasic>();
  const [siteBrandList, setSiteBrandList] = useState<ItemDataType[]>();
  const [expressBasic, setExpressBasic] = useState<ExpressBasic>();
  const [exchangePlatform, setExchangePlatform] = useState<ItemDataType[]>();
  const [casesBasic, setCasesBasic] = useState<CasesBasic>();
  const [loseBasic, setLoseBasic] = useState<LoseBasic>();
  const [receiptBrandList, setReceiptBrandList] = useState<ItemDataType[]>();
  const [serviceBrandList, setServiceBrandList] = useState<ItemDataType[]>();
  const [salesSource, setSalesSource] = useState<ItemDataType[]>();
  const [refuseDesc, setRefuseDesc] = useState<ItemDataType[]>();
  const [ticketList, setTicketList] = useState<ItemDataType[]>();
  const [ticketActList, setTicketActList] = useState<ItemDataType[]>();
  const [subscribeOption, setSubscribeOption] = useState<SubscribeOption>();

  const { run: getProfile, loading: getProfileLoading } = useRequest(
    async () => {
      const res = await fetchGET(API_USER_PROFILE);
      return res.data.data ?? {};
    },
    {
      manual: true,
    }
  );
  const { run: getAllCities, loading: getAllCitiesLoading } = useRequest(fetchAllCities, {
    manual: true,
  });

  const loading = !appReady || getProfileLoading || getAllCitiesLoading;

  const updateProfile = useCallback(
    (profileData: Partial<Profile>) => {
      const newProfileData = {
        ...profile,
        ...profileData,
      };
      setProfile(newProfileData);
    },
    [profile, setProfile]
  );

  useEffect(() => {
    // 加载Profile
    const fetchProfile = async () => {
      const profile = await getProfile();
      setProfile(profile);
      return profile;
    };

    // 加载AllCities
    const fetchCities = async (village) => {
      const cities = await getAllCities(village);
      setAllCities(getCity(cities));
    };

    (async function ready() {
      try {
        const profile = await fetchProfile();
        await fetchCities(profile?.shopping_village);
      } catch (e) {
        console.error(e);
      } finally {
        setAppReady(true);

        // 主应用渲染后移除 loading 界面
        const mask = document.querySelector('.loading');
        mask?.parentNode.removeChild(mask);
      }
    })();
  }, []);

  useEffect(() => {
    //根据登录用户的购物村动态设置页面的favicon，加上随机数防止缓存
    const shopping_village = Number(profile?.shopping_village);
    if (shopping_village) {
      const favicon = document.querySelector('link[rel="shortcut icon"]');
      favicon?.setAttribute(
        'href',
        `${
          process.env.PUBLIC_PATH === '/' ? '' : process.env.PUBLIC_PATH
        }/resources/images/favicon-${Village[shopping_village]}.ico?${Math.random()}`
      );
    }
  }, [profile]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ReadyContext.Provider
      value={{
        profile,
        setProfile: updateProfile,
        allCities,
        setAllCities,
        parkingBasic,
        setParkingBasic,
        accountBasic,
        setAccountBasic,
        blackBookMemberList,
        setBlackBookMemberList,
        mbrCeremonyList,
        setMbrCeremonyList,
        gePcsList,
        setGePcsList,
        serviceBasic,
        setServiceBasic,
        leaseBasic,
        setLeaseBasic,
        siteBrandList,
        setSiteBrandList,
        expressBasic,
        setExpressBasic,
        exchangePlatform,
        setExchangePlatform,
        casesBasic,
        setCasesBasic,
        loseBasic,
        setLoseBasic,
        receiptBrandList,
        setReceiptBrandList,
        serviceBrandList,
        setServiceBrandList,
        salesSource,
        setSalesSource,
        refuseDesc,
        setRefuseDesc,
        ticketList,
        setTicketList,
        ticketActList,
        setTicketActList,
        subscribeOption,
        setSubscribeOption,
      }}
    >
      {children}
    </ReadyContext.Provider>
  );
});
